import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarAPI } from "../api/calendar";
import { isAppointmentGone } from "../booking-errors";
import type {
  BookingTargets,
  CalendarEvent,
  CalendarEventStats,
  CreateEventRequest,
  UpdateEventRequest,
} from "../types";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { isDemoMode } from "@/features/utils/demo-mode";
import { mockEvents, mockCalendarEventStats } from "./use-events.mock";

// Query keys
export const eventsKeys = {
  all: ["calendar_events"] as const,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  events: (clientId: string, options?: any) =>
    [...eventsKeys.all, "events", clientId, options] as const,
  event: (clientId: string, eventId: string) =>
    [...eventsKeys.all, "events", clientId, eventId] as const,
};

// Query hooks
export function useEvents(options?: {
  customer_id?: string;
  timeMin?: number;
  timeMax?: number;
  limit?: number;
  offset?: number;
}) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<CalendarEvent[]>({
    queryKey: eventsKeys.events(clientId, options),
    queryFn: () => {
      if (isDemoMode()) {
        let events = mockEvents;
        if (options?.customer_id) {
          events = events.filter((e) => e.customer_id === options.customer_id);
        }
        if (options?.timeMin) {
          events = events.filter((e) => e.start_time >= options.timeMin!);
        }
        if (options?.timeMax) {
          events = events.filter((e) => e.start_time <= options.timeMax!);
        }
        if (options?.limit) {
          events = events.slice(0, options.limit);
        }
        return Promise.resolve(events);
      }
      return CalendarAPI.listEvents(clientId, options);
    },
    enabled: !!clientId,
    staleTime: 30 * 1000,
  });
}

export function useEvent(eventId: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<CalendarEvent>({
    queryKey: eventsKeys.event(clientId, eventId),
    queryFn: () => {
      if (isDemoMode()) {
        const event = mockEvents.find((e) => e.id === eventId);
        return Promise.resolve(event!);
      }
      return CalendarAPI.getEvent(clientId, eventId);
    },
    enabled: !!clientId && !!eventId,
    staleTime: 30 * 1000,
  });
}

/** Where an appointment created by hand can be booked, and the default choice. */
export function useBookingTargets(enabled = true) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<BookingTargets>({
    queryKey: [...eventsKeys.all, "booking-targets", clientId],
    queryFn: () =>
      isDemoMode()
        ? Promise.resolve({
            targets: [{ id: "internal", integration: "internal" }],
            default_target: "internal",
          })
        : CalendarAPI.getBookingTargets(clientId),
    enabled: !!clientId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// Mutation hooks
export function useCreateEvent() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEventRequest) =>
      CalendarAPI.createEvent(clientId, data),
    onSuccess: () => {
      // Invalidate all calendar-related queries (calendars, events, availability)
      queryClient.invalidateQueries({
        queryKey: eventsKeys.all,
      });
    },
  });
}

export function useUpdateEvent() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      data,
    }: {
      eventId: string;
      data: UpdateEventRequest;
    }) => CalendarAPI.updateEvent(clientId, eventId, data),
    onSuccess: () => {
      // Invalidate all calendar-related queries (calendars, events, availability)
      queryClient.invalidateQueries({
        queryKey: eventsKeys.all,
      });
    },
    // Refused because the booking system had already cancelled it: the server
    // removed it all the same, and the calendar must stop showing it.
    onError: (error) => {
      if (isAppointmentGone(error)) {
        queryClient.invalidateQueries({ queryKey: eventsKeys.all });
      }
    },
  });
}

export function useDeleteEvent() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => CalendarAPI.deleteEvent(clientId, eventId),
    onSuccess: () => {
      // Invalidate all calendar-related queries (calendars, events, availability)
      queryClient.invalidateQueries({
        queryKey: eventsKeys.all,
      });
    },
    // Refused because the booking system had already cancelled it: the server
    // removed it all the same, and the calendar must stop showing it.
    onError: (error) => {
      if (isAppointmentGone(error)) {
        queryClient.invalidateQueries({ queryKey: eventsKeys.all });
      }
    },
  });
}

// Calendar Stats Hook
export function useCalendarStats() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<CalendarEventStats>({
    queryKey: [...eventsKeys.all, "stats", clientId],
    queryFn: () => {
      if (isDemoMode()) {
        return Promise.resolve(mockCalendarEventStats);
      }
      return CalendarAPI.getStats(clientId);
    },
    enabled: !!clientId,
    staleTime: 30 * 1000,
  });
}
